import { useNavigate, useLocation } from "react-router-dom";
import { adminLogout } from "../../slice/adminUser/adminUser";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchStatusCounts } from "../../slice/order/orderSlice";
import api from "../../api/axios";
import QueueChip from "./component/QueueChip";

const TopHeader = ({ deliveryMode, onModeChange }) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const user = localStorage.getItem("username");
  const { statusCounts } = useSelector((state) => state.orders);

  /* true = full-width nav, false = icon rail (desktop) / closed overlay (mobile).
     Seeded from the saved preference so the toggle icon does not render one way
     and flip on the first effect. Mobile always starts closed. */
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined" || window.innerWidth <= 991) return false;
    try { return localStorage.getItem("mly-nav-compact") !== "1"; } catch { return true; }
  });

  /* what the toggle's chevron and its label describe */
  const compact = !sidebarOpen;

  /* Theme: "light" is the golden parchment set, "dark" the deep green one.
     public/index.html already stamped data-theme before paint; read it back
     so the button label matches what is on screen. */
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute("data-theme") || "light"
  );

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem("milliyo-theme", next);
    } catch {
      /* private mode — the theme just won't persist across reloads */
    }
  };

  const handleModeChange = (mode) => {
    onModeChange?.(mode);
    if (!location.pathname.includes("/admin/order/recevied")) {
      navigate("/admin/order/recevied");
    }
  };

  // Fetch pending counts for both delivery modes
  useEffect(() => {
    dispatch(
      fetchStatusCounts({
        deliveryMode: "delivery",
        statuses: ["pending"],
      })
    );

    dispatch(
      fetchStatusCounts({
        deliveryMode: "pickup",
        statuses: ["pending"],
      })
    );
  }, [dispatch]);

  const pendingDeliveryCount = statusCounts?.delivery?.pending || 0;
  const pendingPickupCount   = statusCounts?.pickup?.pending   || 0;

  /* ── Orders-today bell badge: GET /admin/orders/activity every 30s ── */
  const [newToday, setNewToday] = useState(0);
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        /* Flat and camelCase, not wrapped in data:
           { latestOrderAt, latestUpdateAt, newCountToday }. Reading the old
           new_count_today here is a silent zero — Number(undefined) is NaN,
           the badge stays hidden and nothing is logged. */
        const res  = await api.get("/admin/orders/activity");
        const data = res.data || {};
        const n = Number(data.newCountToday);
        if (alive && Number.isFinite(n)) setNewToday(n);
      } catch {
        /* activity endpoint unavailable — badge stays hidden */
      }
    };
    poll();
    const timer = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  /* ── Open support tickets badge: GET /admin/support/tickets?status=open,in_progress ── */
  const [openTickets, setOpenTickets] = useState(0);
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        /* Still wrapped: { data: rows }, and there is no pagination object
           to fall back on — the badge counts the rows it is given. The
           server pages this at 50 unless asked otherwise, so it is asked for
           the maximum 200 rather than reporting "50" forever. */
        const res  = await api.get("/admin/support/tickets?status=open,in_progress&limit=200");
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        const n = list.length;
        if (alive && Number.isFinite(n)) setOpenTickets(n);
      } catch {
        /* support endpoint unavailable — badge stays hidden */
      }
    };
    poll();
    const timer = setInterval(poll, 30000);
    return () => { alive = false; clearInterval(timer); };
  }, []);

  /* Sessions are server-side now: the token stays usable until it expires
     unless the session behind it is revoked, so signing out has to tell the
     server. The header is passed explicitly because the request interceptor
     reads localStorage a microtask later, by which point the clear below has
     already run. Fire-and-forget: an unreachable server must not trap
     someone in the portal, and the token expires on its own. */
  const handleLogout = () => {
    const token = localStorage.getItem("token");
    if (token) {
      api
        .post("/session/logout", null, { headers: { Authorization: `Bearer ${token}` } })
        .catch(() => { /* nothing to do — the session lapses when it expires */ });
    }
    dispatch(adminLogout());
    localStorage.clear();
    sessionStorage.clear();
    navigate("/admin/login", { replace: true });
  };

  /* ── DESKTOP: COLLAPSE TO A RAIL, DO NOT HIDE ──────────────────────────
     The old toggle slid the whole panel off-screen (left:-290px), which left
     the operator with no navigation at all until they found the button
     again. On a desktop it now narrows to an icon rail instead: every
     destination stays one click away, and the labels come back as hover
     tooltips / flyouts (the html.mly-compact rules in Sidebar.jsx).

     Mobile keeps the slide-away: a 76px rail on a phone would eat a third of
     the screen to show nothing readable, and there the panel is an overlay
     that is meant to be dismissed. */
  const FULL_W = 280;
  const RAIL_W = 76;

  const applySidebarStyles = (isOpen) => {
    const isMobile = window.innerWidth <= 991;

    const sidebar    = document.querySelector(".page-wrapper.compact-wrapper .page-body-wrapper div.sidebar-wrapper");
    const pageBody   = document.querySelector(".page-wrapper.compact-wrapper .page-body-wrapper .page-body");
    const pageHeader = document.querySelector(".page-wrapper.compact-wrapper .page-header");
    const footer     = document.querySelector(".footer");

    const backBtn = document.querySelector(".back-btn .fa");
    if (backBtn) {
      backBtn.style.transform = isOpen ? "rotate(0deg)" : "rotate(180deg)";
    }

    if (isMobile) {
      if (isOpen) {
        if (sidebar) sidebar.style.left = "0px";
      } else {
        if (sidebar) sidebar.style.left = "-290px";
      }
    } else {
      /* isOpen === true means FULL width; false now means the icon rail */
      const w = isOpen ? FULL_W : RAIL_W;

      document.documentElement.classList.toggle("mly-compact", !isOpen);

      if (sidebar) {
        sidebar.style.left  = "0px";          // the rail never leaves the screen
        sidebar.style.width = `${w}px`;
      }
      if (pageBody)   pageBody.style.marginLeft  = `${w}px`;
      if (pageHeader) {
        pageHeader.style.marginLeft = `${w}px`;
        pageHeader.style.width      = `calc(100% - ${w}px)`;
      }
      if (footer) footer.style.marginLeft = `${w}px`;
    }
  };

  /* the rail is a working preference, not a per-page accident — remember it */
  const handleToggle = () => {
    const isOpen = !sidebarOpen;
    setSidebarOpen(isOpen);
    applySidebarStyles(isOpen);
    try { localStorage.setItem("mly-nav-compact", isOpen ? "0" : "1"); } catch { /* private mode */ }
  };

  /* Restore the saved choice once, on mount. Desktop only: on a phone the
     panel is an overlay that must start closed whatever was saved. */
  useEffect(() => {
    if (window.innerWidth <= 991) return;
    let saved = null;
    try { saved = localStorage.getItem("mly-nav-compact"); } catch { /* private mode */ }
    const wantsFull = saved !== "1";
    setSidebarOpen(wantsFull);
    applySidebarStyles(wantsFull);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const backBtn       = document.querySelector(".back-btn");
    const handleBackBtn = () => {
      setSidebarOpen(false);
      applySidebarStyles(false);
    };
    if (backBtn) backBtn.addEventListener("click", handleBackBtn);
    return () => {
      if (backBtn) backBtn.removeEventListener("click", handleBackBtn);
    };
  }, []);

  return (
    <div>

      <div className="header-wrapper row m-0">

        {/* ── Logo + Toggle ── */}
        <div className="header-logo-wrapper col-auto p-0">
          <div className="logo-wrapper">
            <a>
              <img className="img-fluid" src="../assets/images/logo/logo.png" alt="" />
            </a>
          </div>

          {/* ── Collapse the nav to an icon rail ──
              Replaces the old four-dot mark, which was decoration with a
              hide-the-whole-sidebar action behind it. This says what it does:
              the panel icon's inner bar shows which way the nav will move, and
              the choice survives a reload (see the mly-compact effect). */}
          <div
            className="toggle-sidebar"
            onClick={handleToggle}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleToggle(); }
            }}
            title={compact ? "Expand menu" : "Collapse menu to icons"}
            aria-label={compact ? "Expand menu" : "Collapse menu to icons"}
            aria-pressed={compact}
            style={{ cursor: "pointer" }}
          >
            <div className="status_toggle sidebar-toggle d-flex">
              <svg width={24} height={24} viewBox="0 0 24 24" fill="none"
                stroke="var(--mly-text)" strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round"
                xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="4" width="18" height="16" rx="2.5" />
                <line x1="9.5" y1="4" x2="9.5" y2="20" />
                {/* chevron points the way the panel is about to travel */}
                {compact
                  ? <polyline points="13.7 9.2 16.5 12 13.7 14.8" />
                  : <polyline points="16.5 9.2 13.7 12 16.5 14.8" />}
              </svg>
            </div>
          </div>
        </div>

        {/* ── What is waiting ──
            One chip rather than two pills; the delivery/pickup split lives
            inside it. component/QueueChip.jsx has the reasoning.        */}
        <div className="left-side-header col ps-0 d-none d-md-block">
          <QueueChip
            mode={deliveryMode}
            onPick={handleModeChange}
            delivery={pendingDeliveryCount}
            pickup={pendingPickupCount}
          />
        </div>

        {/* ── User / Logout ── */}
        <div className="nav-right col-10 col-sm-6 pull-right right-header p-0">
          <ul className="nav-menus">

            {/* ── Light (golden) / Dark (green) theme ── */}
            <li style={{ display: "flex", alignItems: "center", padding: 0 }}>
              <button
                type="button"
                className="mly-theme-toggle"
                onClick={toggleTheme}
                title={theme === "dark" ? "Switch to the golden theme" : "Switch to the green theme"}
              >
                {theme === "dark" ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 7.4a4.6 4.6 0 1 1 0 9.2 4.6 4.6 0 0 1 0-9.2Zm0-6a1.1 1.1 0 0 1 1.1 1.1v1.6a1.1 1.1 0 0 1-2.2 0V2.5A1.1 1.1 0 0 1 12 1.4Zm0 17.5a1.1 1.1 0 0 1 1.1 1.1v1.6a1.1 1.1 0 0 1-2.2 0v-1.6a1.1 1.1 0 0 1 1.1-1.1ZM22.6 12a1.1 1.1 0 0 1-1.1 1.1h-1.6a1.1 1.1 0 0 1 0-2.2h1.6a1.1 1.1 0 0 1 1.1 1.1ZM5.1 12a1.1 1.1 0 0 1-1.1 1.1H2.5a1.1 1.1 0 0 1 0-2.2H4A1.1 1.1 0 0 1 5.1 12Zm14.5-7.6a1.1 1.1 0 0 1 0 1.6l-1.1 1.1a1.1 1.1 0 1 1-1.6-1.6l1.1-1.1a1.1 1.1 0 0 1 1.6 0ZM7.1 16.9a1.1 1.1 0 0 1 0 1.6l-1.1 1.1a1.1 1.1 0 0 1-1.6-1.6l1.1-1.1a1.1 1.1 0 0 1 1.6 0Zm12.5 1.1a1.1 1.1 0 0 1-1.6 1.6l-1.1-1.1a1.1 1.1 0 0 1 1.6-1.6ZM7.1 7.1A1.1 1.1 0 0 1 5.5 8.7L4.4 7.6a1.1 1.1 0 0 1 1.6-1.6Z" /></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M11.3 1.9a1 1 0 0 1 .2 1.2 8 8 0 0 0 11.2 11.2 1 1 0 0 1 1.4 1.2A10.2 10.2 0 1 1 10.1 1.7a1 1 0 0 1 1.2.2Z" /></svg>
                )}
                <span>{theme === "dark" ? "Golden" : "Green"}</span>
              </button>
            </li>

            {/* ── Support tickets shortcut (labelled, with open-ticket badge) ── */}
            <li style={{ display: "flex", alignItems: "center", padding: 0 }}>
              <div
                onClick={() => navigate("/admin/support")}
                title={`${openTickets} open support ticket${openTickets === 1 ? "" : "s"}`}
                style={{
                  position: "relative", cursor: "pointer",
                  marginRight: 16, display: "flex", alignItems: "center", gap: 7,
                  padding: "6px 12px", borderRadius: 30,
                  background: openTickets > 0 ? "#FBECEA" : "var(--mly-surface-2)",
                  border: `1px solid ${openTickets > 0 ? "#E7B7B0" : "var(--mly-border)"}`,
                  color: openTickets > 0 ? "#3A4A34" : "var(--mly-text-soft)",
                  fontWeight: 600, fontSize: 13,
                }}
              >
                {/* life-buoy / support icon */}
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.1a9.9 9.9 0 1 0 0 19.8 9.9 9.9 0 0 0 0-19.8Zm0 3.3a6.6 6.6 0 1 1 0 13.2 6.6 6.6 0 0 1 0-13.2Zm0 2.7a3.9 3.9 0 1 0 0 7.8 3.9 3.9 0 0 0 0-7.8Z" /></svg>
                <span>Support</span>
                {openTickets > 0 && (
                  <span style={{
                    minWidth: 18, height: 18, padding: "0 5px",
                    borderRadius: 10, background: "#C0392B", color: "#fff",
                    fontSize: 10, fontWeight: 700, lineHeight: 1,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {openTickets > 99 ? "99+" : openTickets}
                  </span>
                )}
              </div>
            </li>

            {/* ── New-orders-today bell (unobtrusive) ── */}
            <li style={{ display: "flex", alignItems: "center", padding: 0 }}>
              <div
                onClick={() => navigate("/admin/order/recevied")}
                title={`${newToday} new order${newToday === 1 ? "" : "s"} today`}
                style={{
                  position: "relative", cursor: "pointer",
                  marginRight: 18, display: "flex", alignItems: "center",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="var(--mly-text-soft)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {newToday > 0 && (
                  <span style={{
                    position: "absolute", top: -6, right: -8,
                    minWidth: 16, height: 16, padding: "0 4px",
                    borderRadius: 10, background: "var(--mly-accent-2)", color: "var(--mly-on-accent)",
                    fontSize: 10, fontWeight: 700, lineHeight: 1,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    border: "1.5px solid var(--mly-surface)",
                  }}>
                    {newToday > 99 ? "99+" : newToday}
                  </span>
                )}
              </div>
            </li>

            <li className="profile-nav onhover-dropdown pe-0 py-0 me-0">
              <div className="media profile-media">
                <div style={{
                  width:          32,
                  height:         32,
                  borderRadius:   "50%",
                  background:     "linear-gradient(135deg, var(--milliyo-forest) 0%, var(--milliyo-pine) 100%)",
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "center",
                  color:          "#fff",
                  fontWeight:     "bold",
                  marginRight:    8,
                  fontSize:       14,
                }}>
                  {user?.charAt(0).toUpperCase() || "A"}
                </div>
                <span style={{ fontWeight: 500, fontSize: 14 }}>
                  {user || "Admin"}
                </span>
              </div>

              <ul className="profile-dropdown onhover-show-div">
                <li>
                  <a href="#" onClick={handleLogout}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>
                    <span>Log Out</span>
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </div>

      </div>
    </div>
  );
};

export default TopHeader;